import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, ConfirmPopup } from "@commonsku/styles";
import { BulkActionConfigBase } from "../index";
import { useDispatch } from "react-redux";
import { deleteMultipleVendorCredits } from "../../../../redux/vendor_credits";
import { useReportContext } from "../../report_context";
import { useHasCapabilities } from "../../../../hooks";
import { createExportToAccountingButton } from "../common/bulk-actions";
import { oauth } from "../../../../utils";

export function prepareLabel<
  T extends Record<string, unknown> = Record<string, unknown>
>(selected: T[]): React.ReactNode {
  if (selected.length === 0) {
    return "Select one or more vendor credits";
  }
  if (selected.length === 1) {
    return "1 vendor credit selected";
  }
  return `${selected.length} vendor credits selected`;
}

export function DeleteVendorCreditsButton<T extends Record<string, unknown> = Record<string, unknown>>(
  {
    selected,
    setExecuting,
    disabled,
  }: {
    selected: T[],
    setExecuting: (executing?: boolean) => void,
    disabled?: boolean,
  }
) {
  const canDelete = useHasCapabilities(["DELETE-VENDOR-CREDIT"]);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();
  const { fetchReport } = useReportContext();

  const vendor_credit_ids = useMemo(
    () => selected.map((row: any) => row.form_number.vendor_credit_id),
    [selected]
  );

  const handleDelete = useCallback(async () => {
    setExecuting(true);
    try {
      await dispatch(deleteMultipleVendorCredits(vendor_credit_ids));
      fetchReport();
    } catch (e) {
      toast.error("Unable to delete vendor credits");
    } finally {
      setExecuting(false);
      setShowConfirm(false);
    }
  }, [setExecuting, vendor_credit_ids, dispatch, fetchReport]);

  if (!canDelete) {
    return null;
  }

  return (
    <>
      {showConfirm && (
        <ConfirmPopup
          confirmText="Are you sure you want to delete?  Unpaid commissions will be recalculated."
          onClose={() => setShowConfirm(false)}
          onDelete={handleDelete}
        />
      )}
      <Button
        disabled={disabled}
        onClick={() => setShowConfirm(true)}
        variant="error"
      >
        Delete
      </Button>
    </>
  );
}

export function UnExportVendorCreditsButton<T extends Record<string, unknown> = Record<string, unknown>>(
  {
    selected,
    setExecuting,
    disabled,
  }: {
    selected: T[],
    setExecuting: (executing?: boolean) => void,
    disabled?: boolean,
  }
) {
  const canDelete = useHasCapabilities(["DELETE-VENDOR-CREDIT"]);
  const { fetchReport } = useReportContext();
  const vendor_credit_ids = useMemo(
    () => selected.map((row: any) => row.form_number.vendor_credit_id),
    [selected]
  );

  // Check if any selected vendor credits are exported
  const hasExported = useMemo(
    () => selected.some((row: any) => row.date_exported?.date_exported),
    [selected]
  );

  const handleClick = useCallback(async () => {
    if (vendor_credit_ids.length === 1) {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm("Are you sure to want to un-export this vendor credit?")) {
        return;
      }
    } else {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm("Are you sure to want to un-export these vendor credits?")) {
        return;
      }
    }
    setExecuting(true);
    try {
      await oauth("POST", "vendor-credit", { action: "unexport", ids: vendor_credit_ids });
      if (vendor_credit_ids.length === 1) {
        toast.success("1 vendor credit unexported");
      } else {
        toast.success(`${vendor_credit_ids.length} vendor credits unexported`);
      }
      await fetchReport();
    } catch (e) {
      toast.error("Unable to un-export vendor credits");
    } finally {
      setExecuting(false);
    }
  }, [setExecuting, vendor_credit_ids, fetchReport]);

  if (!canDelete) {
    return null;
  }

  return (
    <Button
      disabled={disabled || !hasExported}
      onClick={handleClick}
    >
      Un-Export
    </Button>
  );
}

const ExportToAccountingButton = createExportToAccountingButton(
  "VENDOR-CREDIT",
  (row: any) => row.form_number.vendor_credit_id,
  false,
  (row: any) => row.date_exported.date_exported,
);

export const BulkActions: BulkActionConfigBase = {
  prepareLabel,
  actions: [
    DeleteVendorCreditsButton,
    UnExportVendorCreditsButton,
    ExportToAccountingButton,
  ]
};
